/**
 * Alcance por usuario (spec 2026-10-05-alcance-sin-token-vinculos).
 *
 * users/{uid}.Alcance = 'empresas' limita un rol level 'all' a las empresas
 * del usuario. En vez de tocar cada `level === 'all'` de la UI, el rol se
 * entrega ya con level 'company' y la marca `scoped`: la UI la trata como
 * usuaria de empresa, y `supervisor` se conserva para carpetas cerradas.
 * Root es global siempre. Mismo criterio que alcanceEmpresas() en
 * firestore.rules y daysi-cloud-functions/scope.js.
 *
 * users/{uid}.SinToken = { document, accounting, linked, xml }: borrar ese
 * tipo de adjunto directo, sin token ni solicitud. Solo root los concede.
 *
 * Módulo puro, para poder testearlo directo.
 */

export const SIN_TOKEN_KINDS = ['document', 'accounting', 'linked', 'xml'];

/**
 * users/{uid}.Carpetas = { abrir, cerrar } (decisión 2026-10-09): permite
 * abrir / cerrar carpetas aunque el rol no lo dé, en todas sus empresas. Solo
 * suma (nunca quita lo que el rol da) y solo lo concede root. No vuelve
 * supervisora al usuario. La matriz de empresa puede decidir distinto en esa
 * empresa (lib/closed_folder_perms.js).
 */
export const CARPETAS_KINDS = ['abrir', 'cerrar'];

const WRITE_FLAGS = ['create', 'update', 'delete'];

// Copia la sección con esos flags en false. Si la sección no existe en el
// rol, no la inventa (un objeto nuevo volvería truthy chequeos de sección).
const denyFlags = (section, flags) => (section
  ? { ...section, ...Object.fromEntries(flags.map((f) => [f, false])) }
  : section);

/**
 * Con alcance a empresas, las pantallas de catálogo global (usuarios,
 * clientes, referencias, plantillas) no aplican: las rules solo las dejan
 * escribir a isSuperAdmin(), que excluye alcanceEmpresas(). Se apagan sus
 * flags para no mostrar menús/botones que la regla rechaza.
 * `customers.read` se conserva: Documentos lista las empresas con él.
 */
const scopeCatalogs = (rol) => {
  const out = { ...rol };
  if (rol.users) out.users = denyFlags(rol.users, ['read', ...WRITE_FLAGS]);
  if (rol.customers) out.customers = denyFlags(rol.customers, WRITE_FLAGS);
  if (rol.references) out.references = denyFlags(rol.references, WRITE_FLAGS);
  if (rol.folders?.templates) {
    out.folders = { ...rol.folders, templates: denyFlags(rol.folders.templates, WRITE_FLAGS) };
  }
  return out;
};

export const applyUserScope = (rolData, userData) => {
  const documents = { ...(rolData?.documents || {}) };
  const scoped = documents.level === 'all'
    && documents.root !== true
    && userData?.Alcance === 'empresas';
  if (scoped) documents.level = 'company';
  documents.scoped = scoped;
  const raw = userData?.SinToken || {};
  const sinToken = Object.fromEntries(SIN_TOKEN_KINDS.map((k) => [k, raw[k] === true]));
  const rawCarp = userData?.Carpetas || {};
  const carpetas = Object.fromEntries(CARPETAS_KINDS.map((k) => [k, rawCarp[k] === true]));
  const base = scoped ? scopeCatalogs(rolData || {}) : { ...(rolData || {}) };
  return { ...base, documents, sinToken, carpetas };
};

/** ¿El rol viene limitado a sus empresas? */
export const isScoped = (globalRole) => globalRole?.documents?.scoped === true;

/**
 * Alcance que se guarda en users/{uid} (decision 2026-10-08).
 * Rol de empresa (documents.level !== 'all': cliente, usuario, auxiliar, admin
 * de empresa) -> siempre 'empresas', sin importar el formulario. Rol global:
 * lo que mande el formulario (solo root lo envia), si no el valor previo, y si
 * tampoco hay, undefined (el campo se omite). Asi quien no es root nunca
 * ensancha: al pasar de rol de empresa a global conserva 'empresas'.
 */
export const resolveAlcance = ({ roleData, formAlcance, previousAlcance } = {}) => {
  if (roleData?.documents?.level !== 'all') return 'empresas';
  return formAlcance || previousAlcance || undefined;
};
